import { Link } from "react-router-dom";
import { Linkedin, Instagram, Youtube, X as XIcon, Mail, Phone, MapPin } from "lucide-react";
import { LogoMark } from "@/components/brand/Logo";
import { Container } from "@/components/layout/Section";
import { SITE, SERVICE_CATEGORIES } from "@/config/site";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const SOCIAL_ICONS = { LinkedIn: Linkedin, X: XIcon, Instagram, YouTube: Youtube };

const companyLinks = [
  { label: "About", href: "/about", testid: "footer-link-about" },
  { label: "Work", href: "/work", testid: "footer-link-work" },
  { label: "Resources", href: "/resources", testid: "footer-link-resources" },
  { label: "Contact", href: "/contact", testid: "footer-link-contact" },
];

const Footer = () => {
  const settings = useSiteSettings();
  return (
  <footer data-testid="site-footer" className="relative overflow-hidden border-t border-brand-mist/10 bg-brand-charcoal">
    <Container className="grid grid-cols-1 gap-12 py-16 sm:py-20 md:grid-cols-2 lg:grid-cols-4">
      <div className="space-y-5">
        <Link to="/" aria-label="A2Z Rankings home" data-testid="footer-logo-link" className="inline-flex items-center gap-2.5">
          <LogoMark size={30} testid="footer-logo-mark" />
          <span className="font-display text-lg font-semibold text-brand-snow">
            A2Z <span className="font-normal text-brand-mist">Rankings</span>
          </span>
        </Link>
        <p className="max-w-xs text-sm leading-relaxed text-brand-mist">
          An international technology, software development and digital growth agency — building for Australian
          businesses and clients worldwide.
        </p>
        <div className="flex gap-3">
          {settings.socials.map((s) => {
            const Icon = SOCIAL_ICONS[s.label];
            return (
              <a
                key={s.label}
                href={s.href}
                aria-label={s.href === "#" ? `${s.label} (profile link coming soon)` : `A2Z Rankings on ${s.label}`}
                data-testid={`footer-social-${s.label.toLowerCase()}`}
                className="rounded-full border border-brand-mist/20 p-2 text-brand-mist transition-colors duration-200 hover:border-brand-emerald hover:text-brand-emerald"
              >
                <Icon size={16} />
              </a>
            );
          })}
        </div>
      </div>

      <nav aria-label="Services" data-testid="footer-services-nav">
        <h3 className="eyebrow mb-5">Services</h3>
        <ul className="space-y-3">
          {SERVICE_CATEGORIES.map((cat) => (
            <li key={cat.slug}>
              <Link
                to={`/services/${cat.slug}`}
                data-testid={`footer-category-${cat.slug}`}
                className="text-sm text-brand-mist transition-colors duration-200 hover:text-brand-snow"
              >
                {cat.name}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <nav aria-label="Company" data-testid="footer-company-nav">
        <h3 className="eyebrow mb-5">Company</h3>
        <ul className="space-y-3">
          {companyLinks.map((link) => (
            <li key={link.href}>
              <Link to={link.href} data-testid={link.testid} className="text-sm text-brand-mist transition-colors duration-200 hover:text-brand-snow">
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <div data-testid="footer-contact-block">
        <h3 className="eyebrow mb-5">Contact</h3>
        <ul className="space-y-4 text-sm text-brand-mist">
          <li>
            <a href={`mailto:${settings.email}`} data-testid="footer-email-link" className="inline-flex items-center gap-2 transition-colors hover:text-brand-emerald">
              <Mail size={14} className="shrink-0 text-brand-emerald" /> {settings.email}
            </a>
          </li>
          <li>
            <a href={settings.phoneHref} data-testid="footer-phone-link" className="inline-flex items-center gap-2 transition-colors hover:text-brand-emerald">
              <Phone size={14} className="shrink-0 text-brand-emerald" /> {settings.phone}
            </a>
          </li>
          <li className="flex items-start gap-2">
            <MapPin size={14} className="mt-0.5 shrink-0 text-brand-emerald" />
            <span data-testid="footer-address">{settings.address}</span>
          </li>
        </ul>
      </div>
    </Container>

    <Container className="flex flex-col gap-4 border-t border-brand-mist/10 py-6 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-xs text-brand-mist/70" data-testid="footer-copyright">
        © {new Date().getFullYear()} A2Z Rankings. All rights reserved.
      </p>
      <nav aria-label="Legal" className="flex flex-wrap gap-x-5 gap-y-2" data-testid="footer-legal-nav">
        <Link to="/privacy-policy" data-testid="footer-legal-privacy" className="text-xs text-brand-mist/70 transition-colors hover:text-brand-emerald">Privacy Policy</Link>
        <Link to="/terms-and-conditions" data-testid="footer-legal-terms" className="text-xs text-brand-mist/70 transition-colors hover:text-brand-emerald">Terms & Conditions</Link>
        <Link to="/refund-and-cancellation-policy" data-testid="footer-legal-refunds" className="text-xs text-brand-mist/70 transition-colors hover:text-brand-emerald">Refunds & Cancellations</Link>
        <Link to="/non-disclosure-agreement" data-testid="footer-legal-nda" className="text-xs text-brand-mist/70 transition-colors hover:text-brand-emerald">Mutual NDA</Link>
      </nav>
      <div className="flex flex-wrap gap-2" data-testid="footer-markets">
        {SITE.markets.map((m, i) => (
          <span
            key={m}
            className={
              i === 0
                ? "rounded-full border border-brand-emerald/40 px-3 py-1 text-[11px] font-medium text-brand-emerald"
                : "rounded-full border border-brand-mist/15 px-3 py-1 text-[11px] text-brand-mist"
            }
          >
            {m}
            {i === 0 ? " · Primary" : ""}
          </span>
        ))}
      </div>
    </Container>

    <div
      aria-hidden="true"
      className="pointer-events-none select-none text-center font-display text-[18vw] font-bold leading-[0.8] text-white/[0.025]"
    >
      A2Z
    </div>
  </footer>
  );
};

export default Footer;
